// SearchAppBar.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  InputBase,
  IconButton,
  Badge,
  Popover,
  Typography,
  Chip,
  Button,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import { RiMessengerLine } from 'react-icons/ri';
import NotificationDropdown from './NotificationDropdown';

const BROWN = '#8A6A3D';
const TEXT_BLACK = '#000000';
const BORDER = 'rgba(138,106,61,0.2)';
const WHITE = '#F8F7F4';
const RED = '#C0392B';

const FILTER_TYPES = ['Buy', 'Rent', 'Lease', 'JV/JD', 'Built to Suit', 'Action Properties'];

// Helper function to reverse geocode coordinates to address
const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    );
    const data = await response.json();
    if (data && data.display_name) {
      return data.display_name;
    }
    return null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

// Helper function to get address from coordinates using Google Maps API (alternative)
const reverseGeocodeGoogle = async (lat, lng) => {
  // If you have Google Maps API key, uncomment and use this
  // const apiKey = 'YOUR_GOOGLE_MAPS_API_KEY';
  // try {
  //   const response = await fetch(
  //     `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
  //   );
  //   const data = await response.json();
  //   if (data.results && data.results.length > 0) {
  //     return data.results[0].formatted_address;
  //   }
  //   return null;
  // } catch (error) {
  //   console.error('Google Geocoding error:', error);
  //   return null;
  // }
  return null;
};

const SearchAppBar = ({
  query,
  setQuery,
  handleKeyPress,
  notificationData,
  handleNotificationClick,
  onApplyFilters,
  locationLabel = 'Your Location',
  locationValue: propLocationValue, // Allow prop to override
  onLocationClick,
  onLocateMeClick,
}) => {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  
  // Location state
  const [locationValue, setLocationValue] = useState(propLocationValue || 'Detecting location...');
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [userAllowedLocation, setUserAllowedLocation] = useState(false);

  const openFilter = Boolean(filterAnchor);

  // Get user's location on component mount
  useEffect(() => {
    // Check if location was previously allowed
    const locationAllowed = localStorage.getItem('locationAllowed') === 'true';
    const savedLocation = localStorage.getItem('userLocation');
    
    if (savedLocation && locationAllowed) {
      setLocationValue(savedLocation);
      setUserAllowedLocation(true);
      return;
    }

    // If location not previously allowed, ask for permission
    if (!locationAllowed) {
      getUserLocation();
    }
  }, []);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setLocationValue('Location unavailable');
      setSnackbarOpen(true);
      return;
    }

    setIsLocationLoading(true);
    setLocationValue('Getting your location...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Try to get address using reverse geocoding
          let address = await reverseGeocode(latitude, longitude);
          
          // If OpenStreetMap fails, try Google Maps (uncomment if you have API key)
          // if (!address) {
          //   address = await reverseGeocodeGoogle(latitude, longitude);
          // }
          
          // If both fail, show coordinates
          if (!address) {
            address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          }
          
          setLocationValue(address);
          setUserAllowedLocation(true);
          setIsLocationLoading(false);
          
          // Save to localStorage
          localStorage.setItem('locationAllowed', 'true');
          localStorage.setItem('userLocation', address);
          localStorage.setItem('userLatitude', latitude.toString());
          localStorage.setItem('userLongitude', longitude.toString());
          
          // Trigger any callback with the location data
          if (onLocateMeClick) {
            onLocateMeClick({ latitude, longitude, address });
          }
          
          setSnackbarOpen(true);
        } catch (error) {
          console.error('Error getting location:', error);
          setLocationError('Failed to get your location');
          setLocationValue('Location unavailable');
          setIsLocationLoading(false);
          setSnackbarOpen(true);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to get your location';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'An error occurred while getting your location.';
        }
        
        setLocationError(errorMessage);
        setLocationValue('Location unavailable');
        setIsLocationLoading(false);
        setSnackbarOpen(true);
        
        // If user denies permission, save that preference
        if (error.code === error.PERMISSION_DENIED) {
          localStorage.setItem('locationAllowed', 'false');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  const handleLocateMe = (e) => {
    if (e) e.stopPropagation();
    
    // Check if location was previously denied
    const locationAllowed = localStorage.getItem('locationAllowed');
    
    if (locationAllowed === 'false') {
      // If previously denied, ask again with a prompt
      const shouldRetry = window.confirm(
        'Location access was previously denied. Would you like to enable location services to get your current location?'
      );
      if (shouldRetry) {
        getUserLocation();
      }
      return;
    }
    
    getUserLocation();
  };

  const handleFilterOpen = (e) => setFilterAnchor(e.currentTarget);
  const handleFilterClose = () => setFilterAnchor(null);

  const toggleType = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleApply = () => {
    onApplyFilters?.({ types: selectedTypes });
    handleFilterClose();
  };

  const handleClearFilters = () => {
    setSelectedTypes([]);
    onApplyFilters?.({ types: [] });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ 
      width: '100%', 
      minWidth: 0, 
      boxSizing: 'border-box', 
      px: { xs: '3%', sm: '1.5%' }, 
      pt: { xs: '2%', sm: '0.8%' },
      backgroundColor: '#F8F7F4',
    }}>
      {/* ================================================= */}
      {/* LOCATION BAR (DYNAMIC) */}
      {/* ================================================= */}
      <Box
        onClick={onLocationClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          background: WHITE,
          border: `1px solid ${BORDER}`,
          borderRadius: '999px',
          px: { xs: '3%', sm: '1.5%' },
          py: { xs: '1.2%', sm: '0.6%' },
          boxShadow: '0 2px 10px -4px rgba(46,42,34,0.12)',
          mb: { xs: '5px', sm: '6px' },
          cursor: 'pointer',
          opacity: isLocationLoading ? 0.7 : 1,
        }}
      >
        {/* LEFT: PIN + TEXT */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '4px', sm: '6px' },
            minWidth: 0,
            flex: 1,
          }}
        >
          <LocationOnRoundedIcon
            sx={{
              color: isLocationLoading ? '#999' : BROWN,
              fontSize: { xs: '0.85rem', sm: '1rem' },
              flexShrink: 0,
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: '0.4rem', sm: '0.5rem' },
                color: '#8A8A8A',
                lineHeight: 1,
              }}
            >
              {isLocationLoading ? 'Detecting...' : locationLabel}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '0.55rem', sm: '0.65rem' },
                fontWeight: 700,
                color: isLocationLoading ? '#999' : TEXT_BLACK,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {locationValue}
            </Typography>
          </Box>
        </Box>

        {/* RIGHT: Dropdown + GPS */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '4px', sm: '6px' },
            flexShrink: 0,
          }}
        >
          <KeyboardArrowDownRoundedIcon
            sx={{
              color: '#8A8A8A',
              fontSize: { xs: '0.85rem', sm: '1rem' },
              flexShrink: 0,
            }}
          />

          <IconButton
            size="small"
            onClick={handleLocateMe}
            disabled={isLocationLoading}
            sx={{
              width: { xs: 22, sm: 28 },
              height: { xs: 22, sm: 28 },
              border: `1px solid ${BORDER}`,
              borderRadius: '50%',
              padding: '2px',
              '&:disabled': {
                opacity: 0.5,
              },
            }}
          >
            <MyLocationRoundedIcon
              sx={{
                color: isLocationLoading ? '#999' : BROWN,
                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                animation: isLocationLoading ? 'pulse 1.5s ease-in-out infinite' : 'none',
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* ================================================= */}
      {/* SEARCH ROW */}
      {/* ================================================= */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: '6px', sm: '8px' },
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: WHITE,
            border: `1px solid ${BORDER}`,
            borderRadius: '999px',
            px: '3%',
            py: { xs: '1.2%', sm: '0.6%' },
            boxShadow: '0 2px 10px -4px rgba(46,42,34,0.12)',
            minWidth: 0,
          }}
        >
          <SearchRoundedIcon sx={{ color: TEXT_BLACK, flexShrink: 0, fontSize: '0.95rem' }} />
          <InputBase
            placeholder="Search... Buy, Rent, Lease, JD/JV, Built to Suit"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: { xs: '0.6rem', sm: '0.7rem' },
              color: '#2E2A22',
              '& input::placeholder': { color: '#9e9e9e', opacity: 1 },
            }}
          />
          <IconButton onClick={handleFilterOpen} size="small" sx={{ flexShrink: 0, p: '2px' }}>
            <TuneRoundedIcon sx={{ color: TEXT_BLACK, fontSize: '0.95rem' }} />
          </IconButton>
        </Box>

        {/* Wishlist */}
        <Box
          sx={{
            width: { xs: 30, sm: 36 },
            height: { xs: 30, sm: 36 },
            borderRadius: '50%',
            background: WHITE,
            border: `1px solid ${BORDER}`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .MuiIconButton-root': { p: 0 },
          }}
        >
          <NotificationDropdown
            icon={<FavoriteBorderRoundedIcon sx={{ fontSize: '1.4rem', color: RED }} />}
            badgeContent={notificationData?.unread_count_liked || 0}
            notifications={notificationData?.notifications_liked}
            title="Wishlist"
            emptyMessage="No items in wishlist"
            onNotificationClick={handleNotificationClick}
          />
        </Box>

        {/* Messages */}
        <Box
          sx={{
            width: { xs: 30, sm: 36 },
            height: { xs: 30, sm: 36 },
            borderRadius: '50%',
            background: WHITE,
            border: `1px solid ${BORDER}`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .MuiIconButton-root': { p: 0 },
          }}
        >
          <NotificationDropdown
            icon={<RiMessengerLine style={{ fontSize: '1.4rem', color: BROWN }} />}
            badgeContent={notificationData?.unread_count_chat || 0}
            notifications={notificationData?.notifications_chat}
            title="Messages"
            emptyMessage="No new messages"
            onNotificationClick={handleNotificationClick}
          />
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={openFilter}
        anchorEl={filterAnchor}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: '14px', p: '18px', width: 280, mt: '8px' } }}
      >
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: TEXT_BLACK, mb: '10px' }}>
          Filter Properties
        </Typography>

        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#6B6350', mb: '6px' }}>
          Property Type
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mb: '14px' }}>
          {FILTER_TYPES.map((type) => (
            <Chip
              key={type}
              label={type}
              onClick={() => toggleType(type)}
              sx={{
                fontSize: '0.68rem',
                fontWeight: 600,
                background: selectedTypes.includes(type) ? BROWN : '#F3EAD6',
                color: selectedTypes.includes(type) ? WHITE : '#2E2A22',
                '&:hover': { background: selectedTypes.includes(type) ? '#75542D' : '#ECE0C6' },
              }}
            />
          ))}
        </Box>

        <Divider sx={{ mb: '12px' }} />

        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Button
            fullWidth
            onClick={handleClearFilters}
            sx={{ 
              textTransform: 'none', 
              fontSize: '0.72rem', 
              fontWeight: 700, 
              color: TEXT_BLACK, 
              border: `1px solid ${BORDER}`, 
              borderRadius: '8px' 
            }}
          >
            Clear
          </Button>
          <Button
            fullWidth
            onClick={handleApply}
            sx={{ 
              textTransform: 'none', 
              fontSize: '0.72rem', 
              fontWeight: 700, 
              color: WHITE, 
              background: BROWN, 
              borderRadius: '8px', 
              '&:hover': { background: '#75542D' } 
            }}
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* Snackbar for location feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={locationError ? 'error' : 'success'} 
          sx={{ width: '100%' }}
        >
          {locationError || (userAllowedLocation ? 'Location updated successfully!' : '')}
        </Alert>
      </Snackbar>

      {/* Add animation styles */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.4; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
};

export default SearchAppBar;